import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getAllLenses, getLensTierInfo } from '../data/lensesData';

function LensesLibrary() {
  const [selectedTier, setSelectedTier] = useState('all');
  const allLenses = getAllLenses();
  const tierInfo = getLensTierInfo();

  const filteredLenses = selectedTier === 'all'
    ? allLenses
    : allLenses.filter(lens => lens.tier === selectedTier);

  // Sort: free lenses first, then premium
  const sortedLenses = [...filteredLenses].sort((a, b) => {
    if (a.tier === 'free' && b.tier !== 'free') return -1;
    if (a.tier !== 'free' && b.tier === 'free') return 1;
    return 0;
  });

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Ingredient Lenses | FoodLens AI - Scientific Food Analysis Categories</title>
        <meta name="description" content="Explore FoodLens AI's ingredient analysis lenses. From artificial colors to ultra-processed food markers, each lens is backed by peer-reviewed research and scientific studies." />
        <meta name="keywords" content="food ingredient analysis, artificial colors research, seed oils health, ultra-processed foods, food additive science, nutritional research, food safety studies" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Ingredient Lenses | FoodLens AI" />
        <meta property="og:description" content="Science-backed ingredient analysis categories with peer-reviewed research citations." />
        <meta property="og:url" content="https://foodlens-ai.com/lenses" />
        <link rel="canonical" href="https://foodlens-ai.com/lenses" />
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-hero py-16 md:py-24">
        <div className="section text-center">
          <h1 className="section-heading">Ingredient Analysis Lenses</h1>
          <p className="section-subheading mx-auto">
            FoodLens uses research-backed "lenses" to analyze food ingredients. Each lens focuses on a specific category of ingredients with documented health concerns.
          </p>
        </div>
      </section>

      <div className="section py-12">
        {/* Tier Filter */}
        <div className="mb-10">
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={() => setSelectedTier('all')}
              className={`px-5 py-2.5 rounded-xl font-medium transition-all ${
                selectedTier === 'all'
                  ? 'bg-sage-500 text-white shadow-soft'
                  : 'bg-surface text-gray-500 hover:bg-sage-50 hover:text-sage-600 border border-sage-100'
              }`}
            >
              All Lenses ({allLenses.length})
            </button>
            <button
              onClick={() => setSelectedTier('free')}
              className={`px-5 py-2.5 rounded-xl font-medium transition-all ${
                selectedTier === 'free'
                  ? 'bg-emerald-500 text-white shadow-soft'
                  : 'bg-surface text-gray-500 hover:bg-emerald-50 hover:text-emerald-600 border border-sage-100'
              }`}
            >
              Free Lenses ({tierInfo.free.lenses.length})
            </button>
            <button
              onClick={() => setSelectedTier('premium')}
              className={`px-5 py-2.5 rounded-xl font-medium transition-all ${
                selectedTier === 'premium'
                  ? 'bg-purple-500 text-white shadow-soft'
                  : 'bg-surface text-gray-500 hover:bg-purple-50 hover:text-purple-600 border border-sage-100'
              }`}
            >
              Premium Lenses ({tierInfo.premium.lenses.length})
            </button>
          </div>
        </div>

        {/* Tier Description */}
        {selectedTier !== 'all' && (
          <div className={`mb-10 p-6 rounded-2xl ${
            selectedTier === 'free' ? 'bg-emerald-100' : 'bg-purple-100'
          }`}>
            <h2 className="font-display text-2xl text-charcoal mb-2">
              {tierInfo[selectedTier].name} Lenses
            </h2>
            <p className="text-gray-600">
              {tierInfo[selectedTier].description}
            </p>
          </div>
        )}

        {/* Lenses Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {sortedLenses.map((lens, index) => (
            <Link
              key={lens.id}
              to={`/lenses/${lens.id}`}
              className="bg-surface border border-sage-100 rounded-2xl p-6 hover:shadow-elevated hover:-translate-y-1 transition-all duration-300 animate-fade-in-up relative"
              style={{ animationDelay: `${index * 30}ms` }}
            >
              {/* Tier Badge */}
              <div className="absolute top-4 right-4">
                <span className={`inline-block px-2 py-1 rounded-lg text-xs font-medium ${
                  lens.tier === 'free'
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-purple-100 text-purple-700'
                }`}>
                  {lens.tier === 'free' ? 'Free' : 'Premium'}
                </span>
              </div>

              <div className="flex items-start mb-3">
                <span className="text-3xl mr-3">{lens.icon}</span>
                <div>
                  <h3 className="font-display text-xl text-charcoal">
                    {lens.name}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {lens.tagline}
                  </p>
                </div>
              </div>

              <p className="text-gray-500 text-sm mb-4 line-clamp-3 leading-relaxed">
                {lens.description}
              </p>

              <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                <span>
                  {lens.ingredients.length} ingredients tracked
                </span>
                <span>
                  {lens.references.length} research paper{lens.references.length !== 1 ? 's' : ''}
                </span>
              </div>

              <div className="flex items-center text-sage-600 font-medium text-sm">
                View Research
                <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </Link>
          ))}
        </div>

        {/* How It Works Section */}
        <div className="mt-16 bg-sage-50 rounded-3xl p-8 md:p-12">
          <h2 className="font-display text-3xl text-charcoal mb-6 text-center">
            How Lenses Work
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-sage-500 rounded-full flex items-center justify-center text-white text-xl mx-auto mb-4">
                1
              </div>
              <h3 className="font-semibold text-charcoal mb-2">Scan Ingredients</h3>
              <p className="text-gray-500 text-sm">
                Use the FoodLens app or extension to scan any food label or ingredient list.
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-sage-500 rounded-full flex items-center justify-center text-white text-xl mx-auto mb-4">
                2
              </div>
              <h3 className="font-semibold text-charcoal mb-2">Apply Lenses</h3>
              <p className="text-gray-500 text-sm">
                Choose which ingredient categories matter most to you and activate those lenses.
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-sage-500 rounded-full flex items-center justify-center text-white text-xl mx-auto mb-4">
                3
              </div>
              <h3 className="font-semibold text-charcoal mb-2">Get Instant Analysis</h3>
              <p className="text-gray-500 text-sm">
                See exactly which concerning ingredients are in your food, backed by research.
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-16 bg-gradient-sage rounded-3xl p-8 md:p-12 text-center text-white">
          <h2 className="font-display text-3xl mb-4">
            Start Analyzing Your Food
          </h2>
          <p className="text-lg text-white/80 mb-6 max-w-2xl mx-auto">
            FoodLens AI uses these research-backed lenses to instantly identify concerning ingredients in any food product.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/app"
              className="inline-block px-8 py-3 bg-white text-sage-600 font-medium rounded-xl hover:bg-sage-50 transition-colors"
            >
              Download Mobile App
            </Link>
            <Link
              to="/extension"
              className="inline-block px-8 py-3 bg-white/10 text-white font-medium rounded-xl border border-white/30 hover:bg-white/20 transition-colors"
            >
              Get Browser Extension
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LensesLibrary;
